import * as React from 'react';
import { Switch } from '@sm/smapply-ui';
import { Confirm } from 'sma.common/components/modals';
import { Checkbox } from '../Checkbox';
import HtmlTextarea from '../HtmlTextarea';
import InlineText from '../InlineText';
import configuration from './configuration';

const noneStyle = {
	display: 'none'
};

export default class FileUploadOptions extends React.Component {
	switchingFileFormats = false;

	constructor(props) {
		super(props);
		this.state = {
			name: props.name,
			title: props.title,
			description: props.description,
			allowMultiple: props.allowMultiple,
			appendToPrevious: props.appendToPrevious,
			fileFormats: props.fileFormats,
			includeDescription: props.includeDescription,
			limitUploads: props.limitUploads,
			limitFileFormat: props.limitFileFormat,
			max: props.max,
			maxFileSize: props.maxFileSize,
			min: props.min,
			optional: props.optional,
			formErrors: {
				minFiles: '',
				maxFiles: '',
				maxFileSize: '',
				fileFormats: '',
			},
		};

		this.fileExtensionTextarea = React.createRef();

		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleNumberInputChange = this.handleNumberInputChange.bind(this);
		this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
		this.hideOptionGroup = this.toggleOptionGroup.bind(this);
		this.preventEmptyFileFormats = this.preventEmptyFileFormats.bind(this);
		this.handleMouseDownOnSwitch = this.handleMouseDownOnSwitch.bind(this);
	}

	handleInputChange(event) {
		const { value, name } = event.target;

		let { min } = this.state;
		if (name === 'optional') {
			if (value.value) {
				// Question is optional, min should be 0 and disabled
				min = 0;
			} else {
				// Question is required, min has to be greater or equal to 1
				if (min < 1) {
					min = 1;
				}
			}
		}

		this.setState({
			[name]: value,
			min: min
		}, async () => {
			const valid = await this.validateField(name, value);
			if (valid) {
				this.props.handleValueChange(this.state);
			}
		});
	}

	handleNumberInputChange(event) {
		const { name } = event.target;
		let { value } = event.target;

		value = parseInt(value);
		let updateValue = value;
		if (isNaN(value)) {
			// Allow the user to erase data
			updateValue = '';
		}
		this.setState({
			[name]: updateValue
		}, async () => {
			const valid = await this.validateField(name, value);
			if (valid) {
				this.props.handleValueChange(this.state);
			}
		});
	}


	/**
	 * Will handle the change events from checkboxes
	 *
	 * @param {Event} event
	 * @memberof FileUploadOptions
	 */
	handleCheckboxChange(event) {
		const { checked, name } = event.target;

		this.setState({
			[name]: checked
		}, () => {
			if (name === 'limitFileFormat') {
				if (checked === true) {
					this.fileExtensionTextarea.current.focus();
				} else {
					// If un-checked, remove errors
					this.state.formErrors.fileFormats = '';
				}
			}
			this.props.handleValueChange(this.state);
		});
	}

	/**
	 * This function will handle toggling (expanding - minimizing) an option group.
	 * In theory this would be generalized into another React Component, but there
	 * exists similar components (cards / accordion) in WDS and smapply-ui.  Since
	 * these libaries aren't used and don't match the styles of the form builder this
	 * will live here until we migrate to those libraries / UIs.
	 *
	 * @param {*} event
	 * @memberof FileUploadOptions
	 */
	toggleOptionGroup(event) {
		const optionGroup = event.target.parentElement.parentElement;
		const optionGroupContent = event.target.parentElement.nextSibling;
		let expand;

		if (optionGroup.classList.contains('collapsed')) {
			expand = true;
		} else {
			expand = false;
		}

		if (expand) {
			optionGroup.classList.remove('collapsed');
			optionGroupContent.style.display = 'unset';
		} else {
			optionGroup.classList.add('collapsed');
			optionGroupContent.style.display = 'none';
		}
	}

	validateField(name, value) {

		let fieldValidationErrors = this.state.formErrors;
		const validateMin = (minValue) => {
			let valid = true;
			// min must be greater than 0 (or 1 if required) and smaller or equal to max
			let absoluteMin = 0;
			if (this.state.allowMultiple && this.state.limitUploads && !this.state.optional.value) {
				absoluteMin = 1;
			}
			if (isNaN(minValue)) {
				valid = false;
				fieldValidationErrors.minFiles = gettext(
					'Minimum value must be a number and cannot be blank'
				);
			} else if (minValue < absoluteMin || minValue > this.state.max) {
				valid = false;
				fieldValidationErrors.minFiles = interpolate(
					gettext(
						'Minimum value must be greater or equal to %(absoluteMin)s and smaller or equal to maximum'
					), {absoluteMin}, true
				);
			} else {
				// Remove the error
				fieldValidationErrors.minFiles = '';
			}
			return valid;
		};

		const validateMax = (maxValue) => {
			let valid = true;
			// max must be greater or equal to min
			if (isNaN(maxValue)) {
				valid = false;
				fieldValidationErrors.maxfiles = gettext(
					'Maximum value must be a number and cannot be blank'
				);
			} else if (maxValue <= 0) {
				valid = false;
				fieldValidationErrors.maxFiles = gettext(
					'Maximum value must be greater than 0'
				);
			} else if (maxValue < this.state.min) {
				valid = false;
				fieldValidationErrors.maxFiles = gettext(
					'Maximum value must be greater or equal to the minimum value'
				);
			} else {
				fieldValidationErrors.maxFiles = '';
			}
			return valid;
		};

		return new Promise((resolve) => {
			let valid = true;
			switch (name) {
			case 'min': {
				valid = validateMin(value) && validateMax(this.state.max);
				break;
			}
			case 'max':
				valid = validateMax(value) && validateMin(this.state.min);
				break;
			case 'maxFileSize':
				if (isNaN(value)) {
					valid = false;
					fieldValidationErrors.maxFileSize = gettext(
						'Maximum file size must be a number and is required'
					);
				} else if (value < 1 || value > configuration.defaults.maxFileSize) {
					// Must be greater than 0 and smaller than max (default)
					valid = false;
					fieldValidationErrors.maxFileSize = interpolate(
						gettext(
							'Maximum file size must be between 0 and %(maxFileSize)s'
						), { maxFileSize: configuration.defaults.maxFileSize }, true
					);
				} else {
					fieldValidationErrors.maxFileSize = '';
				}
				break;
			case 'fileFormats': {
				// Must have at least 1 value
				const validFormatRegex = /(^\.[0-9A-Za-z]+$)/;
				if (value !== undefined) {
					const fileFormats = value.split(',');
					if (fileFormats.length > 0 && fileFormats[0].trim() !== '') {
						fileFormats.map((fileFormat) => {
							return fileFormat.trim();
						}).forEach((fileFormat) => {
							// Empty values are fine as they are ignored.
							if (fileFormat !== '') {
								const matches = fileFormat.match(validFormatRegex);
								if (!matches) {
									valid = false;
									fieldValidationErrors.fileFormats = interpolate(
										gettext(
											'\'%(fileFormat)s\' does not match a valid file format'
										), { fileFormat }, true
									);
								} else if (matches[0] !== fileFormat) {
									valid = false;
									fieldValidationErrors.fileFormats = interpolate(
										gettext(
											'\'%(fileFormat)s\' is invalid.  File formats should be ' +
											'restricted to alphanumeric characters.'
										), { fileFormat }, true
									);
								}
							}
						});
					} else {
						valid = false;
						fieldValidationErrors.fileFormats = gettext(
							'You must specify at least 1 file format'
						);
					}
				} else {
					// Value is undefined
					valid = false;
					fieldValidationErrors.fileFormats = gettext(
						'You must specify at least 1 file format'
					);
				}

				if (valid) {
					// clear out the error message
					fieldValidationErrors.fileFormats = '';
				}
				break;
			}
			default:
				valid = true;
			}

			this.setState({
				formErrors: fieldValidationErrors,
			});
			resolve(valid);
		});
	}

	/**
	 * Will handle the mouse down event from the parent of the switch to handle
	 * the limiting of the file formats.  This will allow us to prevent the blur
	 * event of taking over when trying to turn off the option.
	 *
	 * @memberof FileUploadOptions
	 */
	handleMouseDownOnSwitch() {
		if (this.state.limitFileFormat) {
			this.switchingFileFormats = true;
		} else {
			this.switchingFileFormats = false;
		}
	}

	/**
	 * This method is to prevent the limited file formats field
	 * from being empty.  This is done on blur of the filed since
	 * validation on the form level is too late as the context will
	 * be lost.
	 *
	 * @param {*} event
	 * @memberof FileUploadOptions
	 */
	preventEmptyFileFormats(event) {

		if (!this.switchingFileFormats) {
			const formatsTextarea = event.target;
			const { name, value } = event.target;
			this.validateField(name, value).then((valid) => {
				if (!valid) {
					new Confirm(
						gettext('Invalid File Formats'),
						gettext('Entered file extensions contain errors.  This is a mandatory field.  ' +
							'Please fix any errors before proceeding.'),
						() => {
							// Turn off limiting file formats
							this.setState({
								limitFileFormat: false,
								fileFormats: '',
							});

							this.props.handleValueChange(this.state);
						},
						{
							yes: gettext('Do not limit file formats'),
							no: gettext('Let me change file format values'),
							noCallback: () => {
								// focus textarea
								formatsTextarea.focus();
							}
						}
					);
				}
			});
		}
		this.switchingFileFormats = false;
	}

	render() {
		return (
			<React.Fragment>
				<div className="optgroup collapsable">
					<h3 className="collapsable-title">
						<a onClick={this.toggleOptionGroup}>
							{gettext('General')}
						</a>
					</h3>
					<div>
						<div className="opt row col-12">
							<InlineText
								label={gettext('Identifier')}
								value={this.state.name}
								name="name"
								change={this.handleInputChange}
							/>
						</div>
						<div className="opt row col-12">
							<HtmlTextarea
								label={gettext('Title')}
								value={this.state.title}
								name="title"
								change={this.handleInputChange}
							/>
						</div>
						<div className="opt row col-12">
							<HtmlTextarea
								label={gettext('Description')}
								value={this.state.description}
								name="description"
								change={this.handleInputChange}
							/>
						</div>
					</div>
				</div>
				<div className="optgroup collapsable collapsed">
					<h3 className="collapsable-title">
						<a onClick={this.toggleOptionGroup}>
							{gettext('Display')}
						</a>
					</h3>
					<div style={noneStyle}>
						<div className="opt row">
							<Checkbox
								label={gettext('Append to previous question')}
								name='appendToPrevious'
								value={this.state.appendToPrevious}
								change={this.handleInputChange}
							/>
						</div>
					</div>
				</div>
				<div className="optgroup collapsable collapsed">
					<h3 className="collapsable-title">
						<a onClick={this.toggleOptionGroup}>
							{gettext('Options')}
						</a>
					</h3>
					<div style={noneStyle}>
						<div className="opt row">
							<Checkbox
								label={gettext('Optional')}
								name="optional"
								value={this.state.optional}
								change={this.handleInputChange}>
							</Checkbox>
						</div>
						<div className="opt row">
							<label className="col-sm-8">
								<strong className="inline">
									{gettext('Include an optional description field')}
								</strong>
							</label>
							<div className="col-sm-4">
								<Switch
									checked={this.state.includeDescription}
									value={this.state.includeDescription}
									name="includeDescription"
									onChange={this.handleCheckboxChange}>
								</Switch>
							</div>
						</div>
						<div className="opt row">
							<label className="col-sm-8">
								<strong className="inline">
									{gettext('Allow multiple uploads')}
								</strong>
							</label>
							<div className="col-sm-4">
								<Switch
									checked={this.state.allowMultiple}
									value={this.state.allowMultiple}
									name="allowMultiple"
									onChange={this.handleCheckboxChange}>
								</Switch>
							</div>
						</div>
						{
							this.state.allowMultiple &&
							<div className="opt row">
								<label className="col-sm-8">
									<strong className="inline">
										{gettext('Set a minimum/maximum number of uploads')}
									</strong>
								</label>
								<div className="col-sm-4">
									<Switch
										checked={this.state.limitUploads}
										value={this.state.limitUploads}
										name="limitUploads"
										onChange={this.handleCheckboxChange}>
									</Switch>
								</div>
							</div>
						}
						{
							this.state.limitUploads && this.state.allowMultiple &&
							<div className="opt row">
								<div className="min col-6">
									<label htmlFor="min">
										{`${gettext('min')}: `}
									</label>
									<input
										name="min"
										type="number"
										min={this.state.optional.value ? 0 : 1}
										max={this.state.max}
										value={this.state.min}
										onChange={this.handleNumberInputChange}
										disabled={this.state.optional.value}
									/>
								</div>
								<div className="max col-6">
									<label htmlFor="max">
										{`${gettext('max')}: `}
									</label>
									<input
										name="max"
										type="number"
										min={this.state.min}
										value={this.state.max}
										onChange={this.handleNumberInputChange} />
								</div>
								<div className="error col-12">
									{
										(this.state.formErrors.minFiles || this.state.formErrors.maxFiles)
										&& (
											<ul className="errorlist">
												{
													this.state.formErrors.minFiles
													&& (
														<li>
															{this.state.formErrors.minFiles}
														</li>
													)
												}
												{
													this.state.formErrors.maxFiles
													&& (
														<li>
															{this.state.formErrors.maxFiles}
														</li>
													)
												}
											</ul>
										)
									}
								</div>
							</div>
						}
						<div className="opt row">
							<div className="max col-12">
								<label htmlFor="max">
									{gettext('Maximum individual file size:')}
								</label>
								<input
									name="maxFileSize"
									type="number"
									min="0"
									max="1000"
									value={this.state.maxFileSize}
									onChange={this.handleNumberInputChange}
								/> MB
								<p>
									{gettext('Maximum 1000MB (1GB)')}
								</p>
							</div>
							<div className="error col-12">
								{
									this.state.formErrors.maxFileSize
									&& (
										<ul className="errorlist">
											<li>
												{this.state.formErrors.maxFileSize}
											</li>
										</ul>
									)
								}
							</div>
						</div>
						<div className="opt row">
							<label className="col-8">
								<strong className="inline">
									{gettext('Limit file formats')}
								</strong>
							</label>
							<div
								className="col-4"
								onMouseDown={this.handleMouseDownOnSwitch}>
								<Switch
									checked={this.state.limitFileFormat}
									value={this.state.limitFileFormat}
									name="limitFileFormat"
									onChange={this.handleCheckboxChange}
								/>
							</div>
						</div>
						{
							this.state.limitFileFormat &&
							<div className="opt row">
								<div className="col-12">
									<p>
										{gettext('Enter the extensions of accepted file formats	separated by a comma (e.g.: .pdf, .doc)')}
									</p>
									<textarea
										ref={this.fileExtensionTextarea}
										className="form-control"
										value={this.state.fileFormats}
										name="fileFormats"
										onChange={this.handleInputChange}
										onBlur={this.preventEmptyFileFormats}>
									</textarea>
								</div>
								<div className="error col-12">
									{
										this.state.formErrors.fileFormats
										&& (
											<ul className="errorlist">
												<li>
													{this.state.formErrors.fileFormats}
												</li>
											</ul>
										)
									}
								</div>
							</div>
						}
					</div>
				</div>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ../apps/chide/products/smapply/static/sma.fs/surveys/form-fields/file-upload/FileUploadOptions.jsx